/**
 * Readiness delta (audit G9) — the per-track movement between two readiness
 * snapshots, shown so a reviewer can see "what moved" after an ingest /
 * promotion (e.g. "证据 45%→51% · 有证据 +1").
 *
 * ADR-0002 red line #3 (Lesson §25.7): the five readiness tracks are NEVER
 * summed. This module produces per-track sibling deltas + the honest legal
 * evidence-coverage percentage — never an aggregate readiness score.
 */
import type { ReadinessSummary } from "../services/api";

export interface ReadinessDelta {
  hasChange: boolean;
  // Per-track applicable-count deltas (honest siblings, never summed).
  legalApplicable: number;
  legalWithEvidence: number;
  evidenceStandard: number;
  marketForce: number;
  ecosystem: number;
  continuingWithObligation: number;
  // Board counters.
  applicable: number;
  hiddenByConfig: number;
  unknownNeedsConfig: number;
  // Legal evidence coverage % = with_evidence / applicable, before → after.
  legalEvidencePercentBefore: number;
  legalEvidencePercentAfter: number;
}

function pct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

export function readinessDelta(
  before: ReadinessSummary,
  after: ReadinessSummary,
): ReadinessDelta {
  const bt = before.tracks;
  const at = after.tracks;
  const d = {
    legalApplicable: at.legal.applicable - bt.legal.applicable,
    legalWithEvidence: at.legal.with_evidence - bt.legal.with_evidence,
    evidenceStandard: at.evidence_standard.applicable - bt.evidence_standard.applicable,
    marketForce: at.market_force.applicable - bt.market_force.applicable,
    ecosystem: at.ecosystem.applicable - bt.ecosystem.applicable,
    continuingWithObligation:
      at.continuing_obligation.with_obligation - bt.continuing_obligation.with_obligation,
    applicable: after.counters.applicable - before.counters.applicable,
    hiddenByConfig: after.counters.hidden_by_config - before.counters.hidden_by_config,
    unknownNeedsConfig: after.counters.unknown_needs_config - before.counters.unknown_needs_config,
    legalEvidencePercentBefore: pct(bt.legal.with_evidence, bt.legal.applicable),
    legalEvidencePercentAfter: pct(at.legal.with_evidence, at.legal.applicable),
  };
  const hasChange =
    d.legalApplicable !== 0 ||
    d.legalWithEvidence !== 0 ||
    d.evidenceStandard !== 0 ||
    d.marketForce !== 0 ||
    d.ecosystem !== 0 ||
    d.continuingWithObligation !== 0 ||
    d.applicable !== 0 ||
    d.hiddenByConfig !== 0 ||
    d.unknownNeedsConfig !== 0 ||
    d.legalEvidencePercentBefore !== d.legalEvidencePercentAfter;
  return { hasChange, ...d };
}

/** Compact, human-readable summary of the non-zero movements (advisory copy). */
export function summarizeReadinessDelta(delta: ReadinessDelta): string {
  const parts: string[] = [];
  if (delta.legalEvidencePercentBefore !== delta.legalEvidencePercentAfter) {
    parts.push(`证据 ${delta.legalEvidencePercentBefore}%→${delta.legalEvidencePercentAfter}%`);
  }
  const counts: Array<[string, number]> = [
    ["适用", delta.applicable],
    ["有证据", delta.legalWithEvidence],
    ["硬法", delta.legalApplicable],
    ["证据标准", delta.evidenceStandard],
    ["市场力", delta.marketForce],
    ["生态", delta.ecosystem],
    ["持续义务", delta.continuingWithObligation],
    ["隐藏", delta.hiddenByConfig],
    ["待配置", delta.unknownNeedsConfig],
  ];
  for (const [label, n] of counts) {
    if (n !== 0) parts.push(`${label} ${n > 0 ? "+" : ""}${n}`);
  }
  return parts.join(" · ");
}
